/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import { Box, theme } from "@nordcloud/gnui";
import {
  EventActionAttempt,
  ResourceExecutionErrorCodeCount,
} from "~/generated/graphql";
import { EventResourcesSection } from "~/components/resourceGroup/EventResourcesSection";
import { isResourceScope } from "~/views/events/helpers";
import { EventEntity } from "~/views/events/types";
import { getActionSettingsColGridSizes } from "../utils";
import { ActionBatchSettingsSection } from "./components/ActionBatchSettingsSection";
import { ActionSettingsSection } from "./components/ActionSettingsSection";

type Props = {
  eventEntity: EventEntity;
  planId?: string;
  attempt?: EventActionAttempt;
  totalCount: number;
  errorCodeCounts: ResourceExecutionErrorCodeCount[];
};

export function EventActionBox({
  eventEntity,
  planId,
  attempt,
  totalCount,
  errorCodeCounts,
}: Props) {
  const isBatchEntity =
    eventEntity?.__typename === "EventActionBatch" ||
    eventEntity?.__typename === "EventViewActionBatch";

  const settingsCol = getActionSettingsColGridSizes(
    isResourceScope(eventEntity)
  );

  return (
    <Row css={{ height: "100%" }}>
      <When condition={isResourceScope(eventEntity)}>
        <Col
          xs={4}
          sm={4}
          md={4}
          lg={8}
          css={{ paddingRight: theme.spacing.spacing01 }}
        >
          <EventResourcesSection
            eventEntity={eventEntity}
            attempt={attempt}
            totalCount={totalCount}
            errorCodeCounts={errorCodeCounts}
          />
        </Col>
      </When>
      <Col
        xs={settingsCol.xs}
        sm={settingsCol.sm}
        md={settingsCol.md}
        lg={settingsCol.lg}
      >
        <Box innerSpacing="spacing00" css={{ height: "100%" }}>
          <If condition={isBatchEntity}>
            <Then>
              <ActionBatchSettingsSection
                eventEntity={eventEntity}
                planId={planId}
                attempt={attempt}
              />
            </Then>
            <Else>
              <ActionSettingsSection
                eventEntity={eventEntity}
                planId={planId}
                attempt={attempt}
              />
            </Else>
          </If>
        </Box>
      </Col>
    </Row>
  );
}
